import { CheckCircle2, Circle, Copy, Loader2, XCircle } from "lucide-react";
import { copyText } from "./util";

const STATE = {
  ok: [<CheckCircle2 key="i" className="w-4 h-4 text-emerald-600" />, "text-emerald-700"],
  propagating: [<Loader2 key="i" className="w-4 h-4 text-sky-600 animate-spin" />, "text-sky-700"],
  incorrect: [<XCircle key="i" className="w-4 h-4 text-red-600" />, "text-red-700"],
  missing: [<Circle key="i" className="w-4 h-4 text-slate-400" />, "text-slate-600"],
  unchecked: [<Circle key="i" className="w-4 h-4 text-slate-300" />, "text-slate-500"],
  waiting: [<Circle key="i" className="w-4 h-4 text-amber-400" />, "text-amber-700"],
};

const Cell = ({ v, testid }) => !v ? <span className="text-xs text-slate-500 italic" data-testid={testid}>Shown once hosting is switched on</span> : (
  <div className="flex items-center gap-2 min-w-0">
    <code className="font-mono text-xs bg-slate-50 rounded px-1.5 py-1 break-all flex-1" data-testid={testid}>{v}</code>
    <button onClick={() => copyText(v, "Copied " + v)} className="shrink-0 p-1.5 rounded-md text-slate-500 hover:text-[#062a20] hover:bg-[#f4efe2]" aria-label={`Copy ${v}`} data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></button>
  </div>);

export function RecordsTable({ records }) {
  const all = records.map((r) => `Type: ${r.type}\tName/Host: ${r.host}\tValue/Points to: ${r.value}\tTTL: ${r.ttl}`).join("\n");
  return (
    <div className="space-y-2" data-testid="domain-records">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-semibold text-[#062a20]">Summary: your DNS records</p>
        <button disabled={records.some((r) => !r.value)} onClick={() => copyText(all, "All records copied")} className="chip disabled:opacity-50" data-testid="domain-copy-all"><Copy className="w-3.5 h-3.5" />Copy all DNS records</button>
      </div>
      <div className="hidden md:grid grid-cols-[104px_1fr_1.6fr_70px_1.4fr] gap-3 px-3 text-[11px] uppercase tracking-wide text-slate-500"><span>Type</span><span>Host / Name</span><span>Value / Points to</span><span>TTL</span><span>Status</span></div>
      {records.map((r, i) => {
        const [icon, cls] = STATE[r.state] || STATE.unchecked;
        return (
          <div key={r.id} className="grid md:grid-cols-[104px_1fr_1.6fr_70px_1.4fr] gap-2 md:gap-3 items-center rounded-xl border border-slate-200 p-3" data-testid={`domain-record-${i}`}>
            <div><span className="md:hidden text-[11px] text-slate-500 mr-1">Type</span><Cell v={r.type} testid={`domain-record-${i}-type`} /></div>
            <div><span className="md:hidden text-[11px] text-slate-500">Host / Name</span><Cell v={r.host} testid={`domain-record-${i}-host`} /></div>
            <div><span className="md:hidden text-[11px] text-slate-500">Value / Points to</span><Cell v={r.value} testid={`domain-record-${i}-value`} /></div>
            <div className="text-xs"><span className="md:hidden text-[11px] text-slate-500 mr-1">TTL</span>{r.ttl}<span className="block text-[10px] text-slate-400">(default)</span></div>
            <div className={`flex items-start gap-1.5 text-xs ${cls}`} data-testid={`domain-record-${i}-state`}>{icon}<span>{r.message}</span></div>
          </div>);
      })}
      <p className="text-xs text-slate-500">"@" means your main domain ({records[0]?.fqdn?.replace(/^[^.]+\./, "")}). Some providers want the Name left empty instead. If you can't choose TTL, leave the default.</p>
    </div>);
}

export function Conflicts({ conflicts }) {
  if (!conflicts?.length) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-1.5" data-testid="domain-conflicts">
      <p className="font-semibold text-red-800 text-sm">Remove or change these old records</p>
      <ul className="list-disc pl-5 text-sm text-red-900 space-y-1">{conflicts.map((c) => <li key={c.type + c.host + c.value}>{c.fix}</li>)}</ul>
      <p className="text-xs text-red-800">Only change the records listed here. Do not delete MX, TXT, SPF or DKIM records: they keep your email working.</p>
    </div>);
}
